import type { ReactNode } from 'react';

import { styleguideColors } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily } from '@kaiizen/kai-shiki/core';

const TASK_HEADER_STYLE = {
  position: 'sticky',
  top: 40,
  zIndex: 5,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  minWidth: 0,
  marginTop: 6,
  padding: '7px 0 8px',
  background: styleguideColors.paper,
  color: styleguideColors.ink,
  fontFamily: styleguideFontFamily.body,
  fontSize: pxToRem(12.5),
  fontWeight: 600,
  lineHeight: 1.4,
  textTransform: 'none' as const,
} as const;

const TITLE_STYLE = {
  minWidth: 0,
  overflow: 'hidden',
  border: 0,
  padding: '1px 3px',
  margin: '-1px -3px',
  background: 'transparent',
  color: 'inherit',
  font: 'inherit',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
} as const;

const META_STYLE = {
  flex: '0 0 auto',
  color: styleguideColors.textSecondary,
  fontFamily: styleguideFontFamily.mono,
  fontSize: pxToRem(10),
  fontWeight: 700,
  letterSpacing: '0.04em',
  whiteSpace: 'nowrap' as const,
} as const;

const EMPTY_STYLE = {
  ...META_STYLE,
  padding: '1px 5px',
  border: `1px solid ${styleguideColors.error}`,
  color: styleguideColors.error,
  fontSize: pxToRem(9),
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
} as const;

/** Renders the compact task landmark used inside a Gallery group sheet. */
export const GalleryTaskHeader = ({
  title,
  photoCount,
  onTitleSelect,
  actions,
}: {
  title: string;
  photoCount: number;
  onTitleSelect?: () => void;
  actions?: ReactNode;
}) => (
  <div data-gallery-task-header='true' style={TASK_HEADER_STYLE}>
    {onTitleSelect
      ? (
        <button
          type='button'
          aria-label={`Open ${title}`}
          onClick={onTitleSelect}
          style={{ ...TITLE_STYLE, cursor: 'pointer' }}
        >
          {title}
        </button>
      )
      : <span style={TITLE_STYLE}>{title}</span>}
    {photoCount ? <span style={META_STYLE}>{photoCount} {photoCount === 1 ? 'photo' : 'photos'}</span> : <span style={EMPTY_STYLE}>No photos</span>}
    <span aria-hidden='true' style={{ flex: 1, minWidth: 12, height: 1, background: styleguideColors.stroke }} />
    {actions ? <span style={{ display: 'flex', alignItems: 'center', flex: '0 0 auto' }}>{actions}</span> : null}
  </div>
);
